import PropTypes from 'prop-types';
import { useState } from 'react';

import { getEnv } from '@ctct/ctct-helpers';
import { trackAction } from '@ctct/distui-helpers';
import { Button, Dialog, Dropdown, IconButton, Menu, TableData, TableRow, Tag } from '@ctct/ui-react';

import EditRoleDialog from './EditRoleDialog';
import AccountSvc from '../../../api/AccountSvc';
import { distUiGoTo } from '../../../api/DistUI';
import MyAccountApiLite from '../../../api/MyAccountApiLite';
import displayLastUpdateTime from '../../../constants/flags';
import userRoles, { getRoleName } from '../../../constants/userRoles';
import './UserRow.scss';

const propTypes = {
  userUuid: PropTypes.string.isRequired,
  name: PropTypes.string,
  email: PropTypes.string,
  status: PropTypes.string.isRequired,
  userRowRole: PropTypes.number.isRequired,
  lastUpdate: PropTypes.string,
  lastLogin: PropTypes.string,
  t: PropTypes.func.isRequired,
  onRefreshUsers: PropTypes.func.isRequired,
  'data-qe-id': PropTypes.string.isRequired,
  setAlertData: PropTypes.func.isRequired,
  allowedUpgrade: PropTypes.bool.isRequired,
  isPro: PropTypes.bool.isRequired,
  isLoggedInUserSiteOwner: PropTypes.bool.isRequired,
};

const defaultProps = {
  name: '',
  email: '',
  lastUpdate: '',
  lastLogin: '',
};

const UserRow = ({
  userUuid,
  name,
  email,
  status,
  userRowRole,
  lastUpdate,
  lastLogin,
  t,
  'data-qe-id': qeId,
  onRefreshUsers,
  setAlertData,
  allowedUpgrade,
  isPro,
  isLoggedInUserSiteOwner,
}) => {
  const [showEditRoleDialog, setShowEditRoleDialog] = useState(false);
  let onReactivateUser; // Pre-declare to avoid errors for circular reference, below

  const [showConfirmDeleteDialog, setShowConfirmDeleteDialog] = useState(false);

  const onDeactivateUser = async () => {
    try {
      await MyAccountApiLite.deactivateUser(userUuid);
      onRefreshUsers();
      setAlertData({
        message: t('users.deactivatedUserAlert', { userText: `${email} - ${name}` }),
        undoAction: onReactivateUser,
      });
    } catch (e) {
      setAlertData({ message: t('users.errorDeactivatingUser'), kind: 'error' });
      // eslint-disable-next-line no-console
      console.error(e);
    }
  };

  const onBuy = () => {
    const encodedReturnUrl = encodeURIComponent(`https://app${getEnv()}constantcontact.com/pages/myaccount/side/users`);

    distUiGoTo('payForServices', { returnURL: encodedReturnUrl });
  };

  onReactivateUser = async () => {
    try {
      await MyAccountApiLite.reactivateUser(userUuid);
      onRefreshUsers();
      setAlertData({
        message: t('users.reactivateUserAlert', { userText: `${email} - ${name}` }),
        undoAction: onDeactivateUser,
      });
    } catch (e) {
      const code = e?.response?.data?.code || '';

      if (code) {
        if (code === 'ENTITLEMENT_ERROR') {
          const description = e?.response?.data?.description || '';
          // parse a string of the form:
          // "Entitlement check failed: USER_MGMT, used=3, allowed=3, serviceFailure=false"
          const values = new URLSearchParams(description.replace(/ /g, '').replace(/,/g, '&').replace(/:/g, '='));
          const used = values.get('used');
          const allowed = values.get('allowed');

          if (used && used === allowed) {
            // out of spots
            setAlertData({
              message: allowedUpgrade ? t('users.noSpotsAlert.fullMayUpgrade') : t('users.noSpotsAlert.full'),
              kind: 'warning',
              actionButtonText: t('users.noSpotsAlert.upgrade'),
              undoAction: allowedUpgrade ? onBuy : null,
            });

            return;
          }
        }

        setAlertData({ message: t('users.noSpotsAlert.errorReactivatingUserWithCode', { code }), kind: 'error' });
      } else {
        setAlertData({ message: t('users.noSpotsAlert.errorReactivatingUser'), kind: 'error' });
      }

      // eslint-disable-next-line no-console
      console.error(e);
    }
  };

  const onConfirmedDeleteUser = async () => {
    try {
      await AccountSvc.deleteUser(userUuid);
      onRefreshUsers();
      setAlertData({ message: t('users.deletedUserAlert', { userText: `${email} - ${name}` }) });
    } catch (e) {
      setAlertData({ message: t('users.errorDeletingUser'), kind: 'error' });
      // eslint-disable-next-line no-console
      console.error(e);
    }
  };

  const onDeleteUser = async () => {
    setShowConfirmDeleteDialog(true);
  };

  const onEditRole = async () => {
    setShowEditRoleDialog(true);
  };

  const onEditRoleSuccess = async () => {
    setShowEditRoleDialog(false);
    onRefreshUsers();
    setAlertData({ message: t('users.success_saving') });
  };

  const onMenuItemChoose = (ev, item) => {
    switch (item.value) {
      case 'editrole':
        trackAction({ action: 'Edit user role' });
        onEditRole();
        break;
      case 'deactivateuser':
        trackAction({ action: 'Deactivate user' });
        onDeactivateUser();
        break;
      case 'reactivateuser':
        trackAction({ action: 'Reactivate user' });
        onReactivateUser();
        break;
      case 'deleteuser':
        trackAction({ action: 'Delete user' });
        onDeleteUser();
        break;
      default:
      // do nothing
    }
  };

  const menuItems = [];

  if (status === 'ENABLED') {
    if (!isPro) {
      menuItems.push({ text: t('users.actions.edit_role'), value: 'editrole' });
    }

    menuItems.push({ text: t('users.actions.deactivate_user'), value: 'deactivateuser' });
  } else {
    // cannot edit role for deactivated user
    menuItems.push({ text: t('users.actions.reactivate_user'), value: 'reactivateuser' });
    // can only delete a deactivated user
    menuItems.push({ text: t('users.actions.delete_user'), value: 'deleteuser' });
  }

  const role = getRoleName(userRowRole, t);
  const lastLoginDate = lastLogin ? new Date(lastLogin).toLocaleString() : null;
  const lastUpdateDate = lastUpdate ? new Date(lastUpdate).toLocaleString() : null;

  return (
    <TableRow>
      {/* TODO: CBU-7341 - Adjust attr names */}
      <TableData data-qe-id={`${qeId}-name`} style={{ maxWidth: '300px', wordWrap: 'break-word' }}>
        {name || userUuid}
      </TableData>
      <TableData data-qe-id={`${qeId}-email`}> {email}</TableData>
      <TableData>
        <Tag
          text={status === 'ENABLED' ? t('users.status.active') : t('users.status.deactivated')}
          kind={status === 'ENABLED' ? 'success' : 'default'}
          data-qe-id={`${qeId}-status`}
        />
      </TableData>
      {!isPro && <TableData data-qe-id={`${qeId}-role`}> {role}</TableData>}
      <TableData data-qe-id={`${qeId}-login`}>{lastLoginDate}</TableData>
      {displayLastUpdateTime && <TableData data-qe-id={`${qeId}-date`}>{lastUpdateDate}</TableData>}
      <TableData contentType="actions">
        {isLoggedInUserSiteOwner && userRowRole !== userRoles.SITE_OWNER && (
          <Dropdown unmountedWhenClosed>
            <IconButton title="Actions" glyph="actions" onClick={() => {}} data-qe-id={`${qeId}-actions-button`} />
            <Menu data-qe-id={`${qeId}-menu`} items={menuItems} onChoose={onMenuItemChoose} />
          </Dropdown>
        )}
      </TableData>
      {showEditRoleDialog && (
        <EditRoleDialog
          name={name || userUuid}
          userUuid={userUuid}
          userRole={userRowRole}
          onClose={() => {
            setShowEditRoleDialog(false);
          }}
          onSuccess={onEditRoleSuccess}
          t={t}
        />
      )}
      {showConfirmDeleteDialog && (
        <Dialog
          kind="prompt"
          data-qe-id="confirm_delete_dialog"
          heading={t('users.confirmDeleteUser.heading')}
          isOpen={showConfirmDeleteDialog}
          footerActions={
            <>
              <Button text={t('users.confirmDeleteUser.cancel')} onClick={() => setShowConfirmDeleteDialog(false)} />
              <Button
                text={t('users.confirmDeleteUser.continue')}
                kind="primary"
                data-qe-id="continue_delete_button"
                onClick={() => {
                  setShowConfirmDeleteDialog(false);
                  onConfirmedDeleteUser();
                }}
              />
            </>
          }
        >
          {t('users.confirmDeleteUser.message', { target: `${email} - ${name}` })}
        </Dialog>
      )}
    </TableRow>
  );
};

UserRow.propTypes = propTypes;
UserRow.defaultProps = defaultProps;
export default UserRow;
